import {
  type CreateTamaguiProps,
  createTamagui,
  createTokens,
  setupDev,
} from "tamagui";

import { config as defaultConfig } from "@tamagui/config/v3";
import { shorthands } from "@tamagui/shorthands";

import { animations } from "./config/animations";
import { bodyFont, fontSize, headingFont } from "./config/fonts";
import { media, mediaQueryDefaultActive } from "./config/media";
import { themes as generatedThemes } from "./themes/theme-generated";
import { color } from "./themes/token-colors";
import { radius } from "./themes/token-radius";
import { size } from "./themes/token-size";
import { space } from "./themes/token-space";
import { zIndex } from "./themes/token-z-index";

// Hold down Option for a second to see some helpful visuals
setupDev({
  visualizer: true,
});

/**
 * This avoids shipping themes as JS. Instead, Tamagui will hydrate them from CSS.
 *
 * Themes:
 * - "light" - Legacy theme with old tokens
 * - "v2" - Temporary name for the new design system theme
 */
const themes =
  process.env.TAMAGUI_OPTIMIZE_THEMES === "true"
    ? ({} as typeof generatedThemes)
    : generatedThemes;

export const config = createTamagui({
  ...defaultConfig,
  themes,
  animations,
  // We only have a single theme, so this is not needed.
  shouldAddPrefersColorThemes: false,
  themeClassNameOnRoot: true,
  mediaQueryDefaultActive,
  shorthands,
  fonts: {
    heading: headingFont,
    body: bodyFont,
  },
  tokens: createTokens({
    color,
    radius,
    zIndex,
    space,
    size,
    fontSize,
  }),
  media,
  settings: {
    allowedStyleValues: "somewhat-strict",
    autocompleteSpecificTokens: "except-special",
    fastSchemeChange: false,
    maxDarkLightNesting: 1,
    onlyAllowShorthands: false,
  },
} satisfies CreateTamaguiProps);

export default config;
